import { Head, useForm } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { accountSettingsPath, menuPath } from "@travily/routes";
import type AccountSettingsProfile from "@travily/types/pages/AccountSettingsProfile";
import type { AvatarColor } from "@ui/components/Avatar";
import { ButtonDock } from "@ui/components/ButtonDock";
import { StandardButton } from "@ui/components/StandardButton";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import { useImageUploadField } from "@/hooks/use-image-upload-field";
import { EditFrame } from "@/layouts/edit-frame";
import PersistentLayout from "@/layouts/persistent-layout";
import { avatarKindFromSelection, selectionFromAvatarKind } from "@/lib/avatar/avatar-kind";
import { selectionFromCover } from "@/lib/cover/cover-preference";
import { firstError } from "@/lib/forms/first-error";
import { usePrecognitionFields } from "@/lib/forms/use-precognition-fields";
import { sanitizeHandle } from "@/lib/handle/sanitize-handle";
import { settingsSections } from "../sections";
import type { Selection } from "./show.view";
import { ProfileEditor } from "./show.view";

const AccountSettingsProfilePage = ({
  name,
  handle: initialHandle,
  bio,
  personal,
  avatar_kind,
  avatar_url,
  cover_image,
  cover_preference,
  cover_map_preview,
  cover_city_name,
  color,
  initial,
  patch_path,
  bio_placeholder,
}: AccountSettingsProfile) => {
  const form = useForm({
    name: name ?? "",
    handle: initialHandle ?? "",
    bio: bio ?? "",
    avatar_kind,
    avatar_signed_id: null as string | null,
    cover_image_signed_id: null as string | null,
    remove_cover_image: false,
    cover_preference,
  })
    .withPrecognition("patch", patch_path)
    .setValidationTimeout(400);

  const formRef = useRef(form);
  formRef.current = form;

  const { bindField, fieldValid } = usePrecognitionFields(form);
  const nameField = bindField("name", { validateOn: "blur" });
  const handleField = bindField("handle", { validateOn: "change", transform: sanitizeHandle });

  const avatarField = useImageUploadField({
    initialPreviewUrl: avatar_kind === "custom" ? avatar_url : null,
    onUploaded: (signedId) => formRef.current.setData("avatar_signed_id", signedId),
    onRemoved: () => formRef.current.setData("avatar_signed_id", null),
  });

  const coverField = useImageUploadField({
    initialPreviewUrl: cover_image?.src ?? null,
    onUploaded: (signedId) => formRef.current.setData("cover_image_signed_id", signedId),
    onRemoved: () => formRef.current.setData("cover_image_signed_id", null),
  });

  const [avatarSelection, setAvatarSelection] = useState<Selection>(selectionFromAvatarKind(avatar_kind));
  const coverMapAvailable = Boolean(cover_map_preview);
  const coverMapDisabledReason = coverMapAvailable
    ? undefined
    : cover_city_name
      ? t("components.views.accounts.profile_form.cover_map_preparing")
      : t("components.views.accounts.profile_form.cover_map_add_trip");
  const [coverSelection, setCoverSelection] = useState<Selection>(
    selectionFromCover(cover_preference, Boolean(cover_image), coverMapAvailable),
  );

  const handleChanged = form.data.handle !== (initialHandle ?? "");

  // ImagePicker's remove button fires onRemove() then onSelectionChange({ type: "default" })
  // synchronously, so this flag is the only thing telling a deleted upload apart from a press
  // on the swoosh tile.
  const coverUploadRemoved = useRef(false);

  const removeCover = () => {
    coverUploadRemoved.current = true;
    coverField.onRemove();
  };

  const selectCover = (selection: Selection) => {
    const removedUpload = coverUploadRemoved.current;
    coverUploadRemoved.current = false;

    if (selection.type === "upload") {
      setCoverSelection(selection);
      formRef.current.setData("remove_cover_image", false);
      formRef.current.setData("cover_preference", "auto");
      return;
    }

    // A custom cover always wins server-side, so it must go for map/swoosh to take effect.
    coverField.setPreview(null);
    formRef.current.setData("cover_image_signed_id", null);
    formRef.current.setData("remove_cover_image", true);

    if (removedUpload) {
      const { cover_preference: preference } = formRef.current.data;
      setCoverSelection(selectionFromCover(preference, false, coverMapAvailable));
      return;
    }

    setCoverSelection(selection);

    if (selection.type === "preset") {
      formRef.current.setData("cover_preference", "auto");
    } else if (coverMapAvailable) {
      // The swoosh tile is a deliberate opt-out only when a map exists; otherwise keep the
      // current preference so clicking it doesn't silently downgrade an 'auto' profile.
      formRef.current.setData("cover_preference", "swoosh");
    }
  };

  const save = () => {
    formRef.current.patch(patch_path, { preserveScroll: "errors" });
  };

  const saveButton = (className: string, size: "56" | "64") => (
    <StandardButton
      className={className}
      color="green"
      isDisabled={!form.isDirty || form.processing}
      onPress={save}
      size={size}
    >
      {t("common.actions.save")}
    </StandardButton>
  );

  return (
    <>
      <Head title={t("web.accounts.settings.profiles.show.meta_title")} />
      <EditFrame
        activeSection="profile"
        actions={
          <ButtonDock
            primaryAction={
              <>
                {saveButton("sm:hidden", "56")}
                {saveButton("hidden sm:flex", "64")}
              </>
            }
          />
        }
        backHref={menuPath()}
        href={accountSettingsPath()}
        navLayout="row"
        sections={settingsSections()}
        title={t("web.accounts.settings.show.title")}
      >
        <ProfileEditor
          avatarSelection={avatarSelection}
          avatarUploadPreview={avatarField.previewUrl}
          bio={form.data.bio}
          bioError={form.invalid("bio") ? firstError(form.errors.bio) : undefined}
          bioPlaceholder={bio_placeholder}
          color={color as AvatarColor}
          coverMapAvailable={coverMapAvailable}
          coverMapDisabledReason={coverMapDisabledReason}
          coverMapPreview={cover_map_preview?.src ?? null}
          coverSelection={coverSelection}
          coverUploadPreview={coverField.previewUrl}
          handle={handleField.value}
          handleChanged={handleChanged}
          handleError={handleField.errorMessage}
          handleValid={fieldValid("handle")}
          initial={initial}
          name={nameField.value}
          nameError={nameField.errorMessage}
          onAvatarFileSelect={avatarField.onFileSelect}
          onAvatarRemove={avatarField.onRemove}
          onAvatarSelectionChange={(selection) => {
            setAvatarSelection(selection);
            formRef.current.setData("avatar_kind", avatarKindFromSelection(selection));
          }}
          onBioChange={(value) => formRef.current.setData("bio", value)}
          onCoverFileSelect={coverField.onFileSelect}
          onCoverRemove={removeCover}
          onCoverSelectionChange={selectCover}
          onHandleBlur={handleField.onBlur}
          onHandleChange={handleField.onChange}
          onNameBlur={nameField.onBlur}
          onNameChange={nameField.onChange}
          personal={personal}
        />
      </EditFrame>
    </>
  );
};

AccountSettingsProfilePage.layout = (page: ReactNode) => <PersistentLayout>{page}</PersistentLayout>;

export default AccountSettingsProfilePage;
